import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactFlow, { Background, Controls, ReactFlowProvider } from 'reactflow';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Typography,
} from '@mui/material';

import CustomNode from '../Builder/Custom';
import GroupNode from '../Builder/Group';

import 'reactflow/dist/style.css';

const nodeTypes = {
  custom: CustomNode,
  group: GroupNode,
};

interface Flow {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'draft';
  createdAt: string;
  updatedAt: string;
  nodesCount: number;
  nodes: any[];
  edges: any[];
  usedNodes: string[];
}

const FlowView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [flow, setFlow] = useState<Flow | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      const savedFlows = JSON.parse(localStorage.getItem('flows') || '[]');
      const foundFlow = savedFlows.find((f: Flow) => f.id === id);
      if (foundFlow) {
        setFlow(foundFlow);
      }
      setLoading(false);
    }
  }, [id]);

  const handleEdit = () => {
    navigate(`/flows/builder/${id}`);
  };

  const handleRun = () => {
    // TODO: Implement run functionality
    // eslint-disable-next-line no-console
    console.log('Running flow:', flow?.name);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'draft':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      case 'draft':
        return 'Draft';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading flow...</Typography>
      </Box>
    );
  }

  if (!flow) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          Flow not found
        </Typography>
        <Button onClick={() => navigate('/flows/list')} sx={{ mt: 2 }}>
          Back to List
        </Button>
      </Box>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <div className="bg-white border-b border-[#E5E7EB] px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/flows/list')}
              sx={{ mr: 2 }}
            >
              Back
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-[#00387D] rounded-lg flex items-center justify-center">
                <PlayIcon className="text-white text-lg" />
              </div>
              <div>
                <h1 className="text-[26px] font-extrabold tracking-[-0.025em] text-[#0C2340]">
                  {flow.name}
                </h1>
                <p className="text-sm text-[#6B6B6B]">{flow.description}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Chip
              label={getStatusLabel(flow.status)}
              color={getStatusColor(flow.status) as any}
              size="medium"
            />
            <Button
              onClick={handleRun}
              variant="contained"
              startIcon={<PlayIcon />}
              sx={{ bgcolor: 'green.600', '&:hover': { bgcolor: 'green.700' } }}
            >
              Run Flow
            </Button>
            <Button
              onClick={handleEdit}
              variant="outlined"
              startIcon={<EditIcon />}
            >
              Edit Flow
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-h-0">
          <ReactFlowProvider>
            <ReactFlow
              nodes={flow.nodes || []}
              edges={flow.edges || []}
              nodeTypes={nodeTypes}
              fitView
              className="bg-gray-50"
              style={{ background: '#f8fafc' }}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
            >
              <Controls className="bg-white shadow-lg rounded-lg border border-[#E5E7EB]" />
              <Background
                color="#94a3b8"
                gap={30}
                size={1}
                className="opacity-80"
              />
            </ReactFlow>
          </ReactFlowProvider>
        </div>

        <Card sx={{ width: 300, m: 2, height: 'fit-content' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Flow Details
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Status:</strong> {getStatusLabel(flow.status)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Nodes:</strong> {flow.nodesCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Connections:</strong> {flow.edges?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Created:</strong>{' '}
                {new Date(flow.createdAt).toLocaleDateString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Updated:</strong>{' '}
                {new Date(flow.updatedAt).toLocaleDateString()}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FlowView;
